'use client';

import { useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import { config } from '@ps/config/client';
import { CommunicationConsent } from '@ps/features/access-control/components/access-guard/communication-consent';
import { TERMS_OF_SERVICE_URL } from '@ps/functions/constants';
import {
  PopStoreExtendedSubscriptionLevel,
  type PopStoreSubscriptionLevel,
} from '@ps/models';
import { PoweredByPopStore } from '@ps/ui/atoms/PoweredByPopStore';
import { Button } from '@ps/ui/atoms/button';
import { Checkbox } from '@ps/ui/atoms/checkbox';
import { Label } from '@ps/ui/atoms/label';
import { Skeleton } from '@ps/ui/atoms/skeleton';
import { useCurrentUserPrivateProfile } from '@ps/ui/contexts/user-profile';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { compareExtendedSubscriptionLevel } from '../../subscription/configuration/functions/subscription-display';
import { useSubscribeContext } from '../contexts/subscribe/context';
import { kCreatorConsentCountryCodes } from '../functions/consentCountries';
import { isDisabledForExtendedSubscriptionLevelUpgrade } from '../functions/subscriptionGating';
import { useFollowCreator } from '../hooks/useFollowCreator';
import {
  buildSubscribeCheckoutUrl,
  useSubscribePicker,
} from '../hooks/useSubscribePicker';
import type { CreatorSubscriptionPlan } from '../types/CreatorSubscriptionPlan';
import type { SelectedPlan } from '../types/SelectedPlan';
import { SubscribeCreatorBanner } from './SubscribeCreatorBanner';
import { SubscribeExpressButton } from './SubscribeExpressButton';
import { SubscriptionCell } from './SubscriptionCell';

interface Props {
  defaultLevel?: PopStoreExtendedSubscriptionLevel;
  minLevel?: PopStoreExtendedSubscriptionLevel;
  /** Render the in-picker ✕ close affordance. Dialog hosts (the `@subscribe`
   *  intercept, the in-place `SubscribeDialog`) show it; the standalone page —
   *  which exits via its own back pill on the bio backdrop — hides it. */
  showCloseButton?: boolean;
}

/**
 * The subscribe tier picker (RN `index` page). Follower/Free is followed inline;
 * paid tiers hand off to the existing `community_membership_subscription`
 * checkout. Selectability + upgrade gating are ported from PopshopRN's
 * `SubscriptionPlanPicker`.
 */
export function SubscribeModal({
  defaultLevel,
  minLevel,
  showCloseButton = true,
}: Props) {
  const {
    creatorInfo,
    title,
    onClose,
    onFollowed,
    successRedirectUrl,
    softCheckoutNav,
    routeCheckoutNav,
    resolveExpressIntent,
  } = useSubscribeContext();
  const router = useRouter();

  const privateProfile = useCurrentUserPrivateProfile();

  const creatorId = creatorInfo.id;

  const { isFollowing, canPerformFollow, pending, follow, unfollow } =
    useFollowCreator(creatorId);

  const {
    loading,
    plans,
    selectedPlan,
    setSelectedPlan,
    currentSubscriptionInfo,
    isUpgrading,
  } = useSubscribePicker({
    creatorId,
    storeFrontName: creatorInfo.name,
    followersSettings: creatorInfo.followersSettings,
    defaultLevel,
    minLevel,
    isFollowing,
  });

  const [offerConsent, setOfferConsent] = useState(true);
  const [phoneConsent, setPhoneConsent] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const showMarketingConsent = kCreatorConsentCountryCodes.has(
    privateProfile?.billingCountryCode ?? '',
  );

  const following = isFollowing === true;
  const isSubscriptionActive = !currentSubscriptionInfo?.canceled;
  // RN `canUnfollow`: a follow-only user (following, no creator subscription).
  const canUnfollow = following && !currentSubscriptionInfo;
  const subscribedLevel = (currentSubscriptionInfo?.subscribedLevel ??
    (following ? PopStoreExtendedSubscriptionLevel.FOLLOWER : undefined)) as
    | PopStoreExtendedSubscriptionLevel
    | undefined;

  function isPlanSubscribed(plan: CreatorSubscriptionPlan): boolean {
    if (plan.level === PopStoreExtendedSubscriptionLevel.FOLLOWER) {
      return following;
    }
    return plan.level === subscribedLevel;
  }

  // Which tiers the user may select. 1:1 with RN's `canSelectPlan`.
  function canSelectPlan(plan: CreatorSubscriptionPlan): boolean {
    // Canceled subscription: any plan when there's no queued change (so the
    // canceled tier can be re-selected to resubscribe); only higher-than-queued
    // when a change is already queued.
    if (currentSubscriptionInfo?.canceled) {
      const queued = currentSubscriptionInfo.queuedSubscription;
      if (!queued) return true;
      // `platform_sub_level` (subscriber/fan/vip) shares its string values with
      // the extended enum's paid subset.
      const queuedLevel =
        (queued.platform_sub_level as unknown as
          | PopStoreExtendedSubscriptionLevel
          | undefined) ?? PopStoreExtendedSubscriptionLevel.FOLLOWER;
      return compareExtendedSubscriptionLevel(plan.level, queuedLevel) > 0;
    }
    if (minLevel != null) {
      return compareExtendedSubscriptionLevel(plan.level, minLevel) >= 0;
    }
    if (isPlanSubscribed(plan)) return false;
    if (subscribedLevel != null) {
      return compareExtendedSubscriptionLevel(plan.level, subscribedLevel) > 0;
    }
    return !isDisabledForExtendedSubscriptionLevelUpgrade(
      currentSubscriptionInfo?.activeSubscriptionLevel as
        | PopStoreExtendedSubscriptionLevel
        | undefined,
      plan.level,
    );
  }

  // Remember the user's last longer-commitment term choice so re-selecting VIP
  // after switching tiers restores it, rather than silently reverting to
  // monthly. Mirrors RN's `preferDiscountForLongerCommitment` ref.
  const preferLongerCommitment = useRef(false);

  function onSelect(plan: CreatorSubscriptionPlan) {
    setSelectedPlan({
      plan,
      discountForLongerCommitment:
        plan.level === PopStoreExtendedSubscriptionLevel.VIP &&
        preferLongerCommitment.current,
    });
  }

  function onDiscountSelect(discount: boolean) {
    if (selectedPlan == null) return;
    preferLongerCommitment.current = discount;
    setSelectedPlan({ ...selectedPlan, discountForLongerCommitment: discount });
  }

  async function onFollowTier() {
    // Anonymous (or self) viewers can't follow — surface it instead of firing a
    // phantom success. (RN's follow path routed anonymous users to sign-in.)
    if (!canPerformFollow) {
      toast.error('Please sign in to follow this creator.');
      return;
    }
    try {
      const followed = await follow({
        receivePromotionalOffers: offerConsent,
        sharePhoneNumber: phoneConsent,
      });
      if (followed) onFollowed?.();
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Could not follow. Please try again.',
      );
    }
  }

  async function onUnfollowTier() {
    try {
      await unfollow();
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Could not unfollow. Please try again.',
      );
    }
  }

  async function onConfirm() {
    if (selectedPlan == null) return;
    // Free tier is handled by its inline Follow button, not this CTA.
    if (
      selectedPlan.plan.level === PopStoreExtendedSubscriptionLevel.FOLLOWER
    ) {
      await onFollowTier();
      return;
    }
    const url = buildSubscribeCheckoutUrl({
      urlCode: creatorInfo.urlCode,
      selected: selectedPlan,
      agreeToOffers: offerConsent,
      consentToSharePhone: phoneConsent,
      successRedirectUrl,
    });
    // PDP surface: soft-navigate so the opener product stays mounted underneath
    // the `@checkout` intercept, letting checkout's success return to it. Checkout
    // is the TOP modal layer (`z-[10100]`), so it's never buried — but still close
    // this picker FIRST (`onClose`) so it doesn't linger behind the checkout.
    // `push` (not `replace`) after unmount is committed synchronously, so it survives.
    if (softCheckoutNav) {
      onClose();
      router.push(url);
      return;
    }
    // Bio subscribe route: soft-navigate to `/checkout`. The isolated
    // `@subscribe` slot keeps this picker mounted underneath the `@checkout`
    // intercept (the bio stays mounted below that). Do NOT call `onClose` here —
    // that's `router.back()`, which would race the checkout nav. `push` keeps
    // `/subscribe` in history, so Back from checkout returns to the subscribe
    // picker over the still-mounted bio.
    if (routeCheckoutNav) {
      router.push(url);
      return;
    }
    // Default (route/account callers): full navigation to the checkout hand-off —
    // the codebase-wide convention for reaching `/checkout` (see `TippingDialog`,
    // `CourseDetailView`, the main page). A document navigation unmounts this
    // modal entirely and lets checkout's server components run on a clean surface
    // (rather than soft-firing the `@checkout/(.)checkout` intercept over a
    // still-mounted subscribe modal). (Contrast the account resubscribe path,
    // which stays SPA because both modals live in the same `@modal` slot.)
    window.location.href = url;
  }

  const confirmDisabled =
    selectedPlan == null ||
    (subscribedLevel === selectedPlan.plan.level && isSubscriptionActive) ||
    !canSelectPlan(selectedPlan.plan) ||
    isDisabledForExtendedSubscriptionLevelUpgrade(
      currentSubscriptionInfo?.activeSubscriptionLevel as
        | PopStoreExtendedSubscriptionLevel
        | undefined,
      selectedPlan.plan.level,
    );

  return (
    // Root padding is vertical-only; each section adds its own `px-4`. This lets
    // the scrollable tier list span the full modal width so its scrollbar hugs
    // the right edge (a list inside the root's `px` floated the scrollbar inward,
    // which looked off).
    <div className="text-content-1 bg-surface-1 flex min-h-0 flex-1 flex-col gap-3 py-4">
      {showCloseButton && (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="bg-raise/90 text-sink absolute right-3 top-3 z-10 flex size-7 items-center justify-center rounded-full transition hover:bg-white"
        >
          <X className="size-4" aria-hidden />
        </button>
      )}

      <h2 className="px-4 pr-8 text-xl font-semibold">{title}</h2>

      <div className="px-4">
        <SubscribeCreatorBanner
          creatorInfo={creatorInfo}
          // Banner "(Following)" shows whenever the viewer follows and unfollows
          // on tap — RN's `FollowingStatus` (gated only by dev/non-prod in RN,
          // shown here). Separate from the cell's Unfollow button, which RN
          // gates to a follow-only viewer via `canUnfollow`.
          showUnfollow={following}
          onUnfollow={onUnfollowTier}
        />
      </div>

      {/* Scrollable tier list — full modal width so the scrollbar sits at the
          right edge; `px-4` insets the cards. `overflow-x-hidden` so a wide
          child never adds a horizontal scrollbar. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden px-4 pb-1">
        <p className="text-content-1 text-xs font-semibold">
          Click and choose a plan from below:
        </p>
        {loading
          ? Array.from({ length: 4 }, (_, i) => (
              <Skeleton
                key={i}
                className="bg-surface-2 h-20 w-full shrink-0 rounded-lg"
              />
            ))
          : plans.map(plan => {
              const isSelected: SelectedPlan | undefined =
                selectedPlan?.plan.level === plan.level
                  ? selectedPlan
                  : undefined;
              return (
                <SubscriptionCell
                  key={plan.level}
                  plan={plan}
                  selected={selectedPlan?.plan.level === plan.level}
                  subscribed={isPlanSubscribed(plan)}
                  isCurrentlySubscribedLevel={plan.level === subscribedLevel}
                  isMinimumSubscriptionLevel={plan.level === minLevel}
                  isSubscriptionActive={isSubscriptionActive}
                  disabled={!canSelectPlan(plan)}
                  discountForLongerCommitment={
                    isSelected?.discountForLongerCommitment
                  }
                  canFollow={!isPlanSubscribed(plan)}
                  canUnfollow={
                    plan.level === PopStoreExtendedSubscriptionLevel.FOLLOWER &&
                    canUnfollow
                  }
                  pending={pending}
                  onSelect={onSelect}
                  onFollow={onFollowTier}
                  onUnfollow={onUnfollowTier}
                  onDiscountForLongerCommitmentSelect={onDiscountSelect}
                />
              );
            })}
      </div>

      {/* consent + CTA footer */}
      <div className="text-content-1 border-line-strong flex flex-col gap-3 border-t px-4 pt-3">
        {showMarketingConsent && (
          <div className="flex items-start gap-2">
            <Checkbox
              id="marketing-consent"
              checked={marketingConsent}
              onCheckedChange={checked => setMarketingConsent(checked === true)}
              className="data-[state=checked]:bg-brand data-[state=checked]:border-brand data-[state=checked]:text-content-on-brand mt-0.5"
            />
            <Label
              htmlFor="marketing-consent"
              className="text-content-1 text-sm font-light leading-[1.2]"
            >
              I consent to receiving marketing messages and content alerts from{' '}
              {creatorInfo.name}.
            </Label>
          </div>
        )}

        <CommunicationConsent
          sellerName={creatorInfo.name}
          offerConsent={offerConsent}
          phoneConsent={phoneConsent}
          onOfferConsentChange={setOfferConsent}
          onPhoneConsentChange={setPhoneConsent}
          labelTextColor="text-content-1"
        />

        <Button
          className="bg-brand hover:bg-brand-hover-hover text-content-on-brand"
          disabled={confirmDisabled}
          onClick={onConfirm}
        >
          {isUpgrading ? 'Upgrade' : 'Subscribe'}
        </Button>

        {/* Express "Subscribe with Apple Pay" — bio route only (the server action
            is injected there), a PAID + selectable tier, matching the current
            plan / commitment / consents. Device-gated; renders nothing otherwise. */}
        {resolveExpressIntent &&
          selectedPlan != null &&
          selectedPlan.plan.level !==
            PopStoreExtendedSubscriptionLevel.FOLLOWER &&
          !confirmDisabled && (
            <SubscribeExpressButton
              resolveExpressIntent={resolveExpressIntent}
              urlCode={creatorInfo.urlCode}
              plan={
                selectedPlan.plan.level as unknown as PopStoreSubscriptionLevel
              }
              commitment={
                selectedPlan.discountForLongerCommitment ? 'long' : 'default'
              }
              agreeToOffers={offerConsent}
              consentToSharePhone={phoneConsent}
              redirectTo={successRedirectUrl}
            />
          )}

        <p className="text-content-1 text-center text-sm">
          By clicking the above button, I agree to{' '}
          <a
            href={`https://${config.domain}/${creatorInfo.urlCode}/privacy-policy`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-text hover:underline"
          >
            {creatorInfo.name}&rsquo;s Privacy Policy
          </a>{' '}
          and to{' '}
          <a
            href={TERMS_OF_SERVICE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-text hover:underline"
          >
            POP.STORE Terms and Services.
          </a>
        </p>

        <p className="text-content-1 text-center text-xs leading-[1.2] tracking-[0.2px]">
          All subscriptions are billed as per selected terms and then renew till
          cancelled.
        </p>
        <PoweredByPopStore className="text-content-4 text-center" />
      </div>
    </div>
  );
}
